import { Controller } from "@hotwired/stimulus"
import { Spring, VelocityTracker, project, rubberband, prefersReducedMotion } from "../lib/spring"

// The admin menu, on a phone.
//
// It used to appear and disappear: a class went on, `display` went to `none`,
// and the CSS transition on the element never had anything to animate. This
// drives it from a position instead. The drawer follows the finger one to one,
// keeps the speed it was thrown at, and lands where the throw was going rather
// than where the finger happened to let go. It can be caught while it is still
// moving and sent back the other way without the motion cutting.
//
// Three ways in and out: the menu button, a swipe from the left edge of the
// screen, and a swipe on the drawer or on the dimmed page behind it. Escape
// closes it and puts focus back on the button it came from.
//
// Above 768px there is no drawer — the sidebar is simply part of the page — so
// everything here is scoped to the phone layout and torn down above it.
export default class extends Controller {
  static targets = ["sidebar", "backdrop", "toggle"]

  // How far from the left edge a swipe may start if it is to open the drawer.
  static EDGE = 24
  // Movement before we decide a drag is sideways and not a scroll. Below this
  // both readings are still live and neither has won.
  static INTENT = 10

  connect() {
    this.phone = window.matchMedia("(max-width: 767px)")
    this.tracker = new VelocityTracker()
    this.drag = null
    this.animation = null
    this.x = 0
    this.destination = 0
    this.draggedAt = 0

    this.onPointerDown = this.pointerDown.bind(this)
    this.onPointerMove = this.pointerMove.bind(this)
    this.onPointerUp = this.pointerUp.bind(this)
    this.onKeydown = this.keydown.bind(this)
    this.onLayout = this.layout.bind(this)

    document.addEventListener("pointerdown", this.onPointerDown)
    document.addEventListener("pointermove", this.onPointerMove, { passive: false })
    document.addEventListener("pointerup", this.onPointerUp)
    document.addEventListener("pointercancel", this.onPointerUp)
    document.addEventListener("keydown", this.onKeydown)
    this.phone.addEventListener("change", this.onLayout)

    this.layout()
  }

  disconnect() {
    document.removeEventListener("pointerdown", this.onPointerDown)
    document.removeEventListener("pointermove", this.onPointerMove)
    document.removeEventListener("pointerup", this.onPointerUp)
    document.removeEventListener("pointercancel", this.onPointerUp)
    document.removeEventListener("keydown", this.onKeydown)
    this.phone.removeEventListener("change", this.onLayout)
    this.animation?.stop()
  }

  // ---- Actions -------------------------------------------------------------

  toggle() {
    if (this.recentlyDragged) return
    this.destination === 0 ? this.close({ focus: true }) : this.open({ focus: true })
  }

  // The dimmed page behind the drawer. A swipe that ends there has already
  // closed it; the click that follows the release must not reopen anything.
  dismiss() {
    if (this.recentlyDragged) return
    this.close()
  }

  keydown(event) {
    if (event.key !== "Escape") return
    if (!this.phone.matches || this.destination !== 0) return
    this.close({ focus: true })
  }

  open({ velocity = 0, focus = false } = {}) {
    this.destination = 0
    this.setExpanded(true)
    this.animateTo(0, velocity)
    if (focus) (this.sidebarTarget.querySelector("a, button, input") ?? this.sidebarTarget).focus()
  }

  close({ velocity = 0, focus = false } = {}) {
    this.destination = this.closedX
    this.setExpanded(false)

    // The drawer is about to become `visibility: hidden`. Anything focused
    // inside it would take the focus ring with it.
    const inside = this.sidebarTarget.contains(document.activeElement)
    if ((focus || inside) && this.hasToggleTarget) this.toggleTarget.focus()

    this.animateTo(this.closedX, velocity)
  }

  // ---- Gesture -------------------------------------------------------------

  pointerDown(event) {
    if (!this.phone.matches) return
    if (event.pointerType === "mouse" && event.button !== 0) return
    // Typing in the menu filter is not a drag.
    if (event.target.closest?.("input, textarea, select")) return

    const isOpen = this.destination === 0
    const onDrawer = this.sidebarTarget.contains(event.target)
    const onBackdrop = this.backdropTarget.contains(event.target)
    const fromEdge = !isOpen && event.clientX <= this.constructor.EDGE

    if (!(fromEdge || (isOpen && (onDrawer || onBackdrop)))) return

    this.drag = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      from: this.x,
      committed: false
    }
  }

  pointerMove(event) {
    const drag = this.drag
    if (!drag || event.pointerId !== drag.id) return

    const dx = event.clientX - drag.x
    const dy = event.clientY - drag.y

    if (!drag.committed) {
      // Both readings — sideways and scroll — stay live until one of them is
      // far enough ahead to be the answer.
      if (Math.abs(dx) < this.constructor.INTENT && Math.abs(dy) < this.constructor.INTENT) return
      if (Math.abs(dx) <= Math.abs(dy)) return (this.drag = null)

      drag.committed = true

      // Catching the drawer in flight starts from where it is on the screen,
      // not from where it was heading.
      this.animation?.stop()
      this.animation = null
      drag.from = this.x
      drag.x = event.clientX
      this.sidebarTarget.style.willChange = "transform"
      this.sidebarTarget.setPointerCapture?.(drag.id)
      this.tracker.reset()
      this.tracker.push(this.x, event.timeStamp)
      return
    }

    if (event.cancelable) event.preventDefault()

    let x = drag.from + (event.clientX - drag.x)
    if (x > 0) x = rubberband(x, this.width)          // resists past wide open
    if (x < this.closedX) x = this.closedX            // nothing left to reveal

    this.place(x)
    this.tracker.push(x, event.timeStamp)
  }

  pointerUp(event) {
    const drag = this.drag
    if (!drag || event.pointerId !== drag.id) return

    this.drag = null
    if (!drag.committed) return

    this.sidebarTarget.releasePointerCapture?.(drag.id)
    this.draggedAt = performance.now()

    // Where the throw was going, not where the finger stopped.
    const velocity = this.tracker.velocity
    const landing = this.x + project(velocity)

    landing > this.closedX / 2 ? this.open({ velocity }) : this.close({ velocity })
  }

  // ---- Motion --------------------------------------------------------------

  animateTo(x, velocity = 0) {
    this.animation?.stop()

    if (prefersReducedMotion()) {
      this.place(x)
      return this.settle()
    }

    this.sidebarTarget.style.willChange = "transform"
    this.animation = new Spring({
      from: this.x,
      to: x,
      velocity,
      // Apple ships 0.8 / 0.3 for a drawer: a little overshoot, because the
      // gesture that got here carried momentum of its own.
      damping: 0.8,
      response: 0.3,
      onUpdate: (value) => this.place(value),
      onRest: () => this.settle()
    })
  }

  settle() {
    this.animation = null
    this.sidebarTarget.style.willChange = ""
  }

  // One position drives all of it: the drawer, how dark the page behind it is,
  // and whether either is on the screen at all.
  place(x) {
    this.x = x

    const progress = Math.max(0, Math.min(1, 1 + x / this.width))
    const showing = progress > 0.002

    this.sidebarTarget.style.transform = `translate3d(${x.toFixed(2)}px, 0, 0)`
    this.sidebarTarget.classList.toggle("is-active", showing)
    this.backdropTarget.classList.toggle("is-active", showing)
    this.backdropTarget.style.opacity = progress.toFixed(3)
  }

  layout() {
    this.animation?.stop()
    this.animation = null
    this.drag = null

    if (this.phone.matches) {
      this.destination = this.closedX
      this.setExpanded(false)
      this.place(this.closedX)
      return
    }

    this.destination = 0
    this.setExpanded(false)
    this.sidebarTarget.style.transform = ""
    this.sidebarTarget.style.willChange = ""
    this.sidebarTarget.classList.remove("is-active")
    this.backdropTarget.classList.remove("is-active")
    this.backdropTarget.style.opacity = ""
  }

  setExpanded(open) {
    if (this.hasToggleTarget) this.toggleTarget.setAttribute("aria-expanded", String(open))
  }

  get width() {
    return this.sidebarTarget.offsetWidth || 312
  }

  get closedX() {
    return -this.width
  }

  get recentlyDragged() {
    return performance.now() - this.draggedAt < 400
  }
}
